import { useEffect, useState } from 'react';
import { Card } from '@plick/ui';
import { IconExternalLink, IconX } from '@tabler/icons-react';
import { Announcement } from '@interfaces/general';
import { AnnouncementOptionButtons } from '../shared/AnnouncementOptionButtons';
import { useRespondAnnouncement } from '../CommunicationsModal/hooks/useRespondAnnouncement';

interface Props {
  announcement: Announcement;
  onClose: () => void;
}

export const CommunicationsCard = ({ announcement, onClose }: Props) => {
  // Instancia local del hook → isSubmitting solo afecta a esta tarjeta.
  const { respond, isSubmitting } = useRespondAnnouncement();

  const bgColor = announcement.bg_color || '#FFFFFF';
  const textColor = announcement.text_color || '#1A1A2E';

  const initialDelay = announcement.delay_before_close ?? 0;
  const [canClose, setCanClose] = useState(initialDelay === 0);

  useEffect(() => {
    if (initialDelay > 0) {
      const t = setTimeout(() => setCanClose(true), initialDelay * 1000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [initialDelay]);

  const isPersistent = announcement.is_persistent === true;
  const showCloseButton = !isPersistent && canClose;

  const hasMainUrl = announcement.url && !announcement.options?.length;

  return (
    <Card padding="0rem" backgroundColor={bgColor} borderRadius="0.75rem">
      <div style={{ color: textColor }} className="relative p-4 sm:p-5 flex flex-col gap-3 overflow-hidden rounded-xl">
        {showCloseButton && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Descartar tarjeta"
            className="absolute top-2 right-2 p-1 rounded hover:opacity-80 transition-opacity"
            style={{ color: textColor }}
          >
            <IconX size={16} />
          </button>
        )}

        <div className="pr-6">
          <h3 className="font-bold text-base sm:text-lg">{announcement.title}</h3>
          {announcement.subtitle && <p className="text-sm opacity-90 mt-0.5">{announcement.subtitle}</p>}
        </div>

        {(announcement.text || announcement.long_description) && (
          <p className="text-sm opacity-80 whitespace-pre-wrap">{announcement.text || announcement.long_description}</p>
        )}

        {announcement.options && announcement.options.length > 0 && (
          <AnnouncementOptionButtons
            options={announcement.options}
            announcementId={announcement.uuid}
            userResponse={announcement.user_response}
            bgColor={bgColor}
            textColor={textColor}
            isSubmitting={isSubmitting}
            variant="card"
            respond={respond}
            onComplete={onClose}
          />
        )}

        {hasMainUrl && (
          <a
            href={announcement.url ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold hover:opacity-80 transition-opacity self-start"
            style={{ color: textColor }}
          >
            Más información
            <IconExternalLink size={14} />
          </a>
        )}
      </div>
    </Card>
  );
};
